'use client';

/**
 * Recipe search bar — URL-param driven, live (debounced) search.
 *
 * Typing updates `?q=…` in the URL after a 300ms debounce so results stream in
 * without the user pressing Enter. Hitting Enter still flushes the pending
 * debounce and pushes immediately. Other URL params (filters, sort) are
 * preserved on every push.
 *
 * Empty input deletes `q` from the URL. A small ✕ clear button appears once
 * there is text and clears the field + URL in one click.
 */

import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { Field, Input } from '@/components/ui/input';
import styles from './search-form.module.css';

interface SearchFormProps {
  defaultValue?: string;
  placeholder?: string;
  label?: string;
  clearLabel?: string;
}

const DEBOUNCE_MS = 300;

export function SearchForm({
  defaultValue = '',
  placeholder,
  label,
  clearLabel,
}: SearchFormProps) {
  const t = useTranslations('search');
  const resolvedPlaceholder = placeholder ?? t('defaultPlaceholder');
  const resolvedLabel = label ?? t('defaultLabel');
  const resolvedClearLabel = clearLabel ?? t('clearSearch');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [value, setValue] = useState(defaultValue);
  const [isPending, startTransition] = useTransition();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPushedRef = useRef<string>(defaultValue);

  // Sync local state when the URL `q` changes from outside (e.g. back button).
  // The ref-guard means this only fires when the URL diverges from our last
  // push — no cascade in the steady state.
  const urlQ = searchParams.get('q') ?? '';
  useEffect(() => {
    if (urlQ !== lastPushedRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue(urlQ);
      lastPushedRef.current = urlQ;
    }
  }, [urlQ]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const pushQuery = (next: string) => {
    if (next === lastPushedRef.current) return;
    lastPushedRef.current = next;
    const params = new URLSearchParams(searchParams.toString());
    if (next) params.set('q', next);
    else params.delete('q');
    const qs = params.toString();
    startTransition(() => {
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => pushQuery(next.trim()), DEBOUNCE_MS);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pushQuery(value.trim());
  };

  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setValue('');
    pushQuery('');
    inputRef.current?.focus();
  };

  return (
    <form role="search" onSubmit={handleSubmit} className={styles.form}>
      <Field htmlFor="recipe-search-input" label={<span className={styles.srOnly}>{resolvedLabel}</span>}>
        <Input
          ref={inputRef}
          id="recipe-search-input"
          type="search"
          name="q"
          value={value}
          onChange={handleChange}
          placeholder={resolvedPlaceholder}
          autoComplete="off"
          aria-busy={isPending}
          leadingIcon="search"
          trailingIcon={value.length > 0 ? 'x' : undefined}
          onTrailingClick={handleClear}
          trailingAriaLabel={resolvedClearLabel}
        />
      </Field>
    </form>
  );
}
